/**
 * Autenticación con Firebase (SDK del cliente).
 *
 * Único módulo que habla directamente con el SDK de Firebase. Restringe el acceso
 * al dominio institucional: una cuenta de Google ajena se desconecta de inmediato
 * y la función lanza.
 *
 * Excepción al contrato de la capa: aquí `loginConGoogle` SÍ lanza, porque el
 * mensaje de rechazo debe llegar al usuario.
 *
 * Capa de servicios: una función por operación del API. No lanza nunca —cada
 * función devuelve un valor neutro ante el fallo—, así que el llamador debe
 * comprobar el resultado, no envolver en try/catch.
 */

import { auth } from '@/firebase'
import { GoogleAuthProvider, signInWithPopup, signOut, User } from 'firebase/auth'

const ALLOWED_DOMAIN = 'udi.edu.co'
const provider = new GoogleAuthProvider()

/**
 * Operación sobre login firebase.
 *
 * @returns User
 */
export async function loginConGoogle(): Promise<User> {
  const result = await signInWithPopup(auth, provider)
  const email = result.user.email ?? ''

  if (!email.endsWith(`@${ALLOWED_DOMAIN}`)) {
    await signOut(auth)
    throw new Error(`Acceso restringido. Solo se permiten cuentas @${ALLOWED_DOMAIN}`)
  }

  return result.user
}

/**
 * Consulta login firebase.
 *
 * @returns string | null
 */
export async function getFirebaseIdToken(): Promise<string | null> {
  if (!auth.currentUser) return null
  return auth.currentUser.getIdToken()
}

// Fuerza una renovación real del token contra Firebase — usar solo en retry de 401
export async function refreshFirebaseIdToken(): Promise<string | null> {
  if (!auth.currentUser) return null
  return auth.currentUser.getIdToken(true)
}

/**
 * Operación sobre login firebase.
 *
 * @returns void
 */
export async function logoutFirebase(): Promise<void> {
  await signOut(auth)
}
